import { Button, Select, buttonVariants, cn } from '@blink/ui';
import { NAV_APPS, navigationLink, type NavApp, type Point } from '@blink/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigation } from 'lucide-react';
import { useState } from 'react';
import { ErrorAlert } from '@/components/common';
import { useI18n } from '@/i18n';
import { api, type GoAnswer } from '@/lib/api';

/** Where the phone is now, or null (no permission, no signal) after a few seconds. */
export function currentLocation(timeoutMs = 6000): Promise<Point | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

const NAV_KEY = 'blink.navApp';

/** The courier's navigation app, remembered on this phone. */
export function useNavApp(): [NavApp, (app: NavApp) => void] {
  const read = (): NavApp => {
    try {
      const v = localStorage.getItem(NAV_KEY);
      return (NAV_APPS as readonly string[]).includes(v ?? '') ? (v as NavApp) : 'google';
    } catch {
      return 'google';
    }
  };
  const [app, setApp] = useState<NavApp>(read);
  return [
    app,
    (next) => {
      setApp(next);
      try {
        localStorage.setItem(NAV_KEY, next);
      } catch {
        // private mode: keep it for this visit only
      }
    },
  ];
}

export function NavAppPicker({ value, onChange }: { value: NavApp; onChange: (app: NavApp) => void }) {
  const { t } = useI18n();
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      {t.route.navWith}
      <Select className="h-9 w-auto text-sm" value={value} onChange={(e) => onChange(e.target.value as NavApp)} data-testid="nav-app">
        {NAV_APPS.map((a) => (
          <option key={a} value={a}>
            {t.navApps[a]}
          </option>
        ))}
      </Select>
    </label>
  );
}

/**
 * "Go": the stop becomes "Going there now" on the server, then the courier opens the
 * navigation app at the pin (a link, so the phone opens the app directly).
 */
export function GoButton({ shipmentId, navApp, className }: { shipmentId: string; navApp: NavApp; className?: string }) {
  const { t, f } = useI18n();
  const queryClient = useQueryClient();
  const go = useMutation({
    mutationFn: () => api<GoAnswer>(`/courier/parcels/${shipmentId}/go`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['courier'] }),
  });
  if (go.data) {
    return go.data.pin ? (
      <div className={cn('space-y-1', className)}>
        <a href={navigationLink(navApp, go.data.pin)} target="_blank" rel="noreferrer" className={cn(buttonVariants(), 'h-12 w-full text-base')} data-testid="open-nav">
          <Navigation className="h-5 w-5" />
          {f(t.route.openNav, { app: t.navApps[navApp] })}
        </a>
        {go.data.approximate ? <p className="text-xs text-warning-foreground" data-testid="nav-approximate">{t.route.approximate}</p> : null}
      </div>
    ) : (
      <p className="text-sm text-warning-foreground" data-testid="no-pin">{t.route.noPin}</p>
    );
  }
  return (
    <div className={className}>
      <Button className="h-12 w-full text-base" disabled={go.isPending} onClick={() => go.mutate()} data-testid="go">
        <Navigation className="h-5 w-5" />
        {t.route.go}
      </Button>
      <ErrorAlert error={go.error} />
    </div>
  );
}
